import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faMapMarkerAlt, faLink } from '@fortawesome/free-solid-svg-icons';
import initialStyles from '../settings/initialStyles';
import { linkIcons, linkText } from '../settings/linkData';
import ThemeConfigPanel from '../settings/ThemeConfigPanel';
import Notification from '../components/notification/Notification'; // Ensure correct import path

import 'react-circular-progressbar/dist/styles.css';
import '../styles/TemplateFour.css';

const themePresets = {
    default: initialStyles,
    dark: {
        backgroundColor: '#333',
        secondaryBackgroundColor: '#555',
        headerBackgroundColor: '#222',
        headerTextColor: '#fff',
        subheaderTextColor: '#555',
        textColor: '#eee',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    },
    light: {
        backgroundColor: '#f9f9f9',
        secondaryBackgroundColor: '#e0e0e0',
        headerBackgroundColor: '#ddd',
        headerTextColor: '#333',
        subheaderTextColor: '#555',
        textColor: '#111',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    }
};

const ViewTemplateFour = ({ template, isDownloading }) => {
    const [styles, setStyles] = useState(initialStyles);
    const [notification, setNotification] = useState(null);

    const handleStyleChange = (e) => {
        const { name, value } = e.target;
        setStyles((prevStyles) => ({
            ...prevStyles,
            [name]: value,
        }));
    };

    const applyPreset = (preset) => {
        setStyles(themePresets[preset]);
    };

    const saveCustomTheme = () => {
        localStorage.setItem('customTheme', JSON.stringify(styles));
        setNotification({ message: 'Custom theme saved!', isError: false });
    };

    const loadCustomTheme = () => {
        const customTheme = localStorage.getItem('customTheme');
        if (customTheme) {
            setStyles(JSON.parse(customTheme));
            setNotification({ message: 'Custom theme loaded!', isError: false });
        } else {
            setNotification({ message: 'No custom theme found!', isError: true });
        }
    };

    const templateData = template || {};

    const skillsWithLevels = (templateData?.skills || []).map(skill => ({
        name: skill,
        level: Math.floor(Math.random() * 11) + 90

    }));

    const softwareSkillsWithLevels = (templateData?.softwareSkills || []).map(skill => ({
        name: skill,
        level: Math.floor(Math.random() * 11) + 90

    }));

    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://venusresume.vercel.app/';
    const imageUrl = templateData?.image?.startsWith('/')
        ? `${baseUrl}${templateData.image}`
        : templateData.image;

    return (
        <div className='holder'>

            {notification && (
                <Notification
                    message={notification.message}
                    isError={notification.isError}
                    onClose={() => setNotification(null)}
                />
            )}

            <div className="template-four-container" style={{ backgroundColor: styles.backgroundColor }}>

                <div className="template-four-header" style={{ backgroundColor: styles.headerBackgroundColor }}>
                    <div className="header-details" >
                        {template?.personName && <h1 className="template-four-name" style={{ fontSize: `${styles.headerFontSize}em`, color: styles.headerTextColor }}>{template?.personName}</h1>}
                    </div>
                    {imageUrl && <img src={imageUrl} alt={template?.personName} className="template-four-image" />}
                </div>

                <div className="template-four-details">

                    {templateData?.contact && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Contact</strong>
                            <div className="template-four-contact-info">
                                {templateData.contact.email && <p><FontAwesomeIcon icon={faEnvelope} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.email}</p>}
                                {templateData.contact.phone && <p><FontAwesomeIcon icon={faPhone} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.phone}</p>}
                                {templateData.contact.address && <p><FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.address}</p>}
                            </div>
                        </div>
                    )}

                    {templateData?.aboutMe && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>About Me:</strong>
                            <p style={{ fontSize: `${styles.bodyFontSize}em`, color: styles.textColor }}>{templateData.aboutMe}</p>
                        </div>
                    )}
                    {templateData?.skills && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Skills:</strong>
                            <div className="template-four-skills-wrapper">
                                {skillsWithLevels.map((skill, index) => (
                                    <div key={index} className="template-four-skill-item">
                                        <span className="template-four-skill-name" style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {templateData?.softwareSkills && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Software Skills:</strong>
                            <div className="template-four-skills-wrapper">
                                {softwareSkillsWithLevels.map((skill, index) => (
                                    <div key={index} className="template-four-skill-item">
                                        <span className="template-four-skill-name" style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {templateData?.workExperience && templateData.workExperience.length > 0 && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Work Experience:</strong>
                            {templateData.workExperience.map((experience, index) => (
                                <div key={index} className="template-four-work-experience-item">
                                    {experience.role && <p className="template-four-role" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.role} at {experience.company}</p>}
                                    {experience.location && (
                                        <p className="location" style={{ fontSize: `${styles.bodyFontSize}em` }}>
                                            {experience.location} (
                                            {experience.startDate && new Date(experience.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                            {experience.endDate !== "Present" ? new Date(experience.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                            )
                                        </p>
                                    )}
                                    {experience.description && <p className="template-four-description" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.description}</p>}
                                </div>
                            ))}
                        </div>
                    )}
                    {templateData?.education && templateData.education.length > 0 && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Education:</strong>
                            {templateData.education.map((edu, index) => (
                                <div key={index} className="template-four-education-item">
                                    {edu.degree && <p className="template-four-degree" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.degree} from {edu.institution}</p>}
                                    <p style={{ fontSize: `${styles.bodyFontSize}em`, fontWeight: "200" }}>
                                        (
                                        {edu.startDate && new Date(edu.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                        {edu.endDate !== "Present" ? new Date(edu.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                        )
                                    </p>

                                    {edu.description && <p className="template-four-description" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.description}</p>}

                                </div>
                            ))}
                        </div>
                    )}

                    {templateData?.certificates && templateData.certificates.length > 0 && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Certificates:</strong>
                            {templateData.certificates.map((cert, index) => (
                                <div key={index} className="template-four-education-item">
                                    {cert.title && <p className="template-four-degree" style={{ fontSize: `${styles.bodyFontSize}em` }}>Title: {cert.title}</p>}
                                    {cert.institution && <p className="template-four-certificate-institution" style={{ fontSize: `${styles.bodyFontSize}em` }}>Institution: {cert.institution}</p>}

                                    {cert.DateReceived && <p className="template-four-certificate-date" style={{ fontSize: `${styles.bodyFontSize}em` }}>
                                        Date Received: {new Date(cert.DateReceived).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </p>}
                                </div>
                            ))}
                        </div>
                    )}

                    {templateData?.languages && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Languages:</strong>
                            <p style={{ fontSize: `${styles.bodyFontSize}em` }}>{templateData.languages.join(', ')}</p>
                        </div>
                    )}
                    {templateData?.hobbies && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Hobbies:</strong>
                            <p style={{ fontSize: `${styles.bodyFontSize}em` }}>{templateData.hobbies.join(', ')}</p>
                        </div>
                    )}

                    {templateData?.links && (
                        <div className="template-four-section">
                            <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Links:</strong>
                            <div className="template-four-links">
                                {Object.entries(templateData.links).map(([key, value]) => (
                                    <p key={key}>
                                        <FontAwesomeIcon icon={linkIcons[key] || faLink} /> <a href={value} target="_blank" rel="noopener noreferrer" style={{ fontSize: `${styles.bodyFontSize}em` }}>{linkText[key]}</a>
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>
            {!isDownloading && (
                <ThemeConfigPanel
                    styles={styles}
                    handleStyleChange={handleStyleChange}
                    applyPreset={applyPreset}
                    saveCustomTheme={saveCustomTheme}
                    loadCustomTheme={loadCustomTheme}
                />
            )}
        </div>
    );
};

export default ViewTemplateFour;
